import * as React from 'react';
import { useNavigate } from 'react-router';
import {
  UploadIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircle2Icon,
  AlertCircleIcon,
  DownloadIcon,
  FileTextIcon,
} from 'lucide-react';
import {
  useUploadImportFile,
  usePreviewImport,
  useCommitImport,
  useGetImportHistory,
  useGetWorkspaceRegions,
} from '@cdf/api-client-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type Step = 'upload' | 'mapping' | 'preview' | 'results';

const ASSET_FIELDS = [
  { value: 'latitude', label: 'Latitude' },
  { value: 'longitude', label: 'Longitude' },
  { value: 'assetTypeId', label: 'Asset Type ID' },
  { value: 'assetTypeName', label: 'Asset Type Name' },
  { value: 'condition', label: 'Condition' },
  { value: 'address', label: 'Address' },
  { value: 'externalSource', label: 'External Source' },
  { value: 'externalId', label: 'External ID' },
  { value: '__skip__', label: '— Skip —' },
];

export function ImportWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = React.useState<Step>('upload');
  const [sessionId, setSessionId] = React.useState('');
  const [externalSource, setExternalSource] = React.useState('');
  const [fileName, setFileName] = React.useState('');
  const [sourceType, setSourceType] = React.useState('');
  const [headers, setHeaders] = React.useState<string[]>([]);
  const [sampleRows, setSampleRows] = React.useState<Record<string, unknown>[]>([]);
  const [suggestedMapping, setSuggestedMapping] = React.useState<Record<string, string>>({});
  const [mapping, setMapping] = React.useState<Record<string, string>>({});
  const [availableAssetTypes, setAvailableAssetTypes] = React.useState<
    Array<{ id: string; name: string; slug: string }>
  >([]);
  const [previewData, setPreviewData] = React.useState<{
    totalRows: number;
    validRows: number;
    errorCount: number;
    errors: Array<{ row: number; message: string }>;
  } | null>(null);
  const [commitResult, setCommitResult] = React.useState<{
    importRunId?: string;
    created: number;
    updated: number;
    skipped: number;
    errors: number;
  } | null>(null);
  const [regionId, setRegionId] = React.useState<string>('none');
  const [error, setError] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const uploadMutation = useUploadImportFile();
  const previewMutation = usePreviewImport();
  const commitMutation = useCommitImport();
  const { data: historyData, refetch: refetchHistory } = useGetImportHistory({ page: 1, limit: 10 });
  const { data: regionsData } = useGetWorkspaceRegions();
  const regions = regionsData?.regions ?? [];

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const result = await uploadMutation.mutateAsync({ data: { file } });
      setSessionId(result.sessionId ?? '');
      setFileName(result.fileName ?? '');
      setSourceType(result.sourceType ?? '');
      setHeaders(result.headers ?? []);
      setSampleRows((result.sampleRows as Record<string, unknown>[]) ?? []);
      setSuggestedMapping((result.suggestedMapping as Record<string, string>) ?? {});
      setMapping((result.suggestedMapping as Record<string, string>) ?? {});
      setAvailableAssetTypes((result.availableAssetTypes as Array<{ id: string; name: string; slug: string }>) ?? []);
      setStep('mapping');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handlePreview = async () => {
    setBusy(true);
    setError('');
    try {
      // Filter out __skip__ mappings
      const cleanMapping: Record<string, string> = {};
      for (const [k, v] of Object.entries(mapping)) {
        if (v && v !== '__skip__') cleanMapping[k] = v;
      }
      const result = await previewMutation.mutateAsync({
        data: {
          sessionId,
          externalSource: externalSource || undefined,
          mapping: cleanMapping,
        },
      });
      setPreviewData({
        totalRows: result.totalRows ?? 0,
        validRows: result.validRows ?? 0,
        errorCount: result.errorCount ?? 0,
        errors: (result.errors ?? []).map((e) => ({ row: e.row ?? 0, message: e.message ?? '' })),
      });
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Preview failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCommit = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await commitMutation.mutateAsync({
        data: {
          sessionId,
          regionId: regionId !== 'none' ? regionId : undefined,
        },
      });
      setCommitResult({
        importRunId: result.importRunId,
        created: result.created ?? 0,
        updated: result.updated ?? 0,
        skipped: result.skipped ?? 0,
        errors: result.errors ?? 0,
      });
      setStep('results');
      refetchHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Commit failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setSessionId('');
    setExternalSource('');
    setFileName('');
    setHeaders([]);
    setSampleRows([]);
    setSuggestedMapping({});
    setMapping({});
    setPreviewData(null);
    setCommitResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const stepNumber = step === 'upload' ? 1 : step === 'mapping' ? 2 : step === 'preview' ? 3 : 4;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Import Assets"
        icon={UploadIcon}
        actions={
          <Button variant="outline" size="sm" onClick={() => navigate('/workspace/assets')}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Assets
          </Button>
        }
      />

      {/* Step indicator */}
      <div className="flex items-center gap-2 text-sm">
        {['Upload', 'Map Columns', 'Validate', 'Results'].map((label, i) => (
          <React.Fragment key={label}>
            <div
              className={`flex items-center gap-2 rounded-full px-3 py-1 ${
                stepNumber === i + 1
                  ? 'bg-primary text-primary-foreground'
                  : stepNumber > i + 1
                    ? 'text-primary'
                    : 'text-muted-foreground'
              }`}
            >
              {stepNumber > i + 1 ? (
                <CheckCircle2Icon className="h-4 w-4" />
              ) : (
                <span className="flex h-5 w-5 items-center justify-center rounded-full border text-xs">
                  {i + 1}
                </span>
              )}
              {label}
            </div>
            {i < 3 && <div className="h-px w-8 bg-border" />}
          </React.Fragment>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200">
          <AlertCircleIcon className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Upload step */}
      {step === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Upload File</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 p-12"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file && fileInputRef.current) {
                  const dt = new DataTransfer();
                  dt.items.add(file);
                  fileInputRef.current.files = dt.files;
                  fileInputRef.current.dispatchEvent(new Event('change'));
                }
              }}
            >
              <UploadIcon className="mb-3 h-10 w-10 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Drag & drop or click to select a file
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Supports .geojson, .json, .csv (max 20MB)
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".geojson,.json,.csv"
                className="hidden"
                onChange={handleFileSelect}
              />
              <Button
                variant="outline"
                className="mt-4"
                disabled={busy}
                onClick={() => fileInputRef.current?.click()}
              >
                {busy ? 'Uploading...' : 'Select File'}
              </Button>
            </div>

            {/* Import history */}
            {historyData && historyData.imports.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold">Recent Imports</h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>File</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Updated</TableHead>
                      <TableHead>Errors</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyData.imports.map((imp) => (
                      <TableRow key={imp.id}>
                        <TableCell>{imp.fileName ?? '—'}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-xs">
                            {imp.sourceType}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={imp.status === 'completed' ? 'default' : 'secondary'}
                            className="text-xs"
                          >
                            {imp.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{imp.createdCount}</TableCell>
                        <TableCell>{imp.updatedCount}</TableCell>
                        <TableCell>{imp.errorCount ?? 0}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {new Date(imp.createdAt ?? '').toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {(imp.errorCount ?? 0) > 0 && (
                            <a
                              href={`/api/workspace/assets/import/${imp.id}/errors`}
                              className="text-primary hover:underline"
                            >
                              <DownloadIcon className="h-4 w-4" />
                            </a>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mapping step */}
      {step === 'mapping' && (
        <Card>
          <CardHeader>
            <CardTitle>Map Columns</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2 text-sm">
              <FileTextIcon className="h-4 w-4" />
              <span>{fileName}</span>
              <Badge variant="secondary" className="text-xs">{sourceType}</Badge>
              <span className="text-muted-foreground">{headers.length} columns detected</span>
            </div>

            {/* External source */}
            <div className="max-w-md">
              <Label>External Source (for upsert tracking)</Label>
              <Input
                value={externalSource}
                onChange={(e) => setExternalSource(e.target.value)}
                placeholder="e.g. council-gis-2024"
                className="mt-1"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Re-importing with the same source + external ID updates existing assets.
              </p>
            </div>

            {/* Column mapping */}
            <div className="space-y-2">
              <Label>Column Mapping</Label>
              <div className="space-y-2">
                {headers.map((header) => {
                  const suggestion = suggestedMapping[header];
                  const mappedValue = mapping[header] ?? '';
                  const hasSuggestion = !!suggestion && suggestion !== '__skip__';
                  return (
                    <div key={header} className="flex items-center gap-3">
                      <div className="w-48 font-mono text-sm">{header}</div>
                      <ArrowRightIcon className="h-4 w-4 text-muted-foreground" />
                      <Select
                        value={mappedValue}
                        onValueChange={(v) =>
                          setMapping((prev) => ({ ...prev, [header]: v }))
                        }
                      >
                        <SelectTrigger className="w-64">
                          <SelectValue placeholder="Select field..." />
                        </SelectTrigger>
                        <SelectContent>
                          {ASSET_FIELDS.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                          {/* Asset type ID suggestions */}
                          {availableAssetTypes.map((at) => (
                            <SelectItem key={at.id} value={`assetTypeId:${at.id}`}>
                              Asset Type: {at.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {hasSuggestion && mappedValue === suggestion && (
                        <Badge className="text-xs">suggested</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Sample data preview */}
            {sampleRows.length > 0 && (
              <div className="space-y-2">
                <Label>Sample Data (first 5 rows)</Label>
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {headers.map((h) => (
                          <TableHead key={h} className="font-mono text-xs">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sampleRows.map((row, i) => (
                        <TableRow key={i}>
                          {headers.map((h) => (
                            <TableCell key={h} className="text-xs">
                              {String(row[h] ?? '—')}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleReset}>
                Cancel
              </Button>
              <Button onClick={handlePreview} disabled={busy}>
                {busy ? 'Validating...' : 'Validate'}
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview step */}
      {step === 'preview' && previewData && (
        <Card>
          <CardHeader>
            <CardTitle>Validation Preview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="py-4">
                  <div className="text-2xl font-bold text-green-600">{previewData.validRows}</div>
                  <div className="text-sm text-muted-foreground">Valid rows</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-4">
                  <div className="text-2xl font-bold text-red-600">{previewData.errorCount}</div>
                  <div className="text-sm text-muted-foreground">Errors</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-4">
                  <div className="text-2xl font-bold">{previewData.totalRows}</div>
                  <div className="text-sm text-muted-foreground">Total rows</div>
                </CardContent>
              </Card>
            </div>

            {/* Region selection */}
            <div className="max-w-md">
              <Label>Assign to Region (optional)</Label>
              <Select value={regionId} onValueChange={setRegionId}>
                <SelectTrigger className="mt-1 w-full">
                  <SelectValue placeholder="No region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No region</SelectItem>
                  {regions.map((r) => (
                    <SelectItem key={r.id ?? ''} value={r.id ?? ''}>
                      {r.name ?? ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Error table */}
            {previewData.errors.length > 0 && (
              <div className="space-y-2">
                <Label>Errors ({previewData.errors.length} rows with issues)</Label>
                <div className="max-h-64 overflow-y-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-16">Row</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.errors.map((err, i) => (
                        <TableRow key={i}>
                          <TableCell className="font-mono">{err.row}</TableCell>
                          <TableCell className="text-sm text-red-600">{err.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setStep('mapping')}>
                <ArrowLeftIcon className="mr-2 h-4 w-4" />
                Back to Mapping
              </Button>
              <Button
                onClick={handleCommit}
                disabled={busy || previewData.validRows === 0}
              >
                {busy ? 'Importing...' : `Import ${previewData.validRows} Assets`}
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results step */}
      {step === 'results' && commitResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2Icon className="h-6 w-6 text-green-600" />
              Import Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="py-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{commitResult.created}</div>
                  <div className="text-sm text-muted-foreground">Created</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{commitResult.updated}</div>
                  <div className="text-sm text-muted-foreground">Updated</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-4 text-center">
                  <div className="text-2xl font-bold text-orange-600">{commitResult.skipped}</div>
                  <div className="text-sm text-muted-foreground">Skipped</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-4 text-center">
                  <div className="text-2xl font-bold text-red-600">{commitResult.errors}</div>
                  <div className="text-sm text-muted-foreground">Errors</div>
                </CardContent>
              </Card>
            </div>

            {(commitResult.errors ?? 0) > 0 && commitResult.importRunId && (
              <Button variant="outline" size="sm" asChild>
                <a href={`/api/workspace/assets/import/${commitResult.importRunId ?? ''}/errors`}>
                  <DownloadIcon className="mr-2 h-4 w-4" />
                  Download Error Report
                </a>
              </Button>
            )}

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleReset}>
                Import Another File
              </Button>
              <Button onClick={() => navigate('/workspace/assets')}>
                View Assets
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

